import React, { useState, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TablePagination,
  Chip,
  Box,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import * as locales from "@material-ui/core/locale";
import { Alert } from "@material-ui/lab";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CancelOutlinedIcon from "@material-ui/icons/CancelOutlined";
import { useObtenerPlanesClienteQuery } from "../../../services/accesoClientesApi";
import Scrollbar from "../../Scrollbar";
import TableSkeleton from "src/components/cliente/loading/TableSkeleton";
import EmptyState from "src/components/cliente/empty/EmptyState";
import StatusBadge from "src/components/cliente/badges/StatusBadge";
import { formatValorPlan } from "src/utils/formatNumber";
import { formatearFecha } from "src/utils/formatTime";

const styles = makeStyles((theme) => ({
  root: {
    minWidth: 275,
  },
  title: {
    textAlign: "center",
    fontSize: "24px",
    marginBottom: theme.spacing(1),
  },
  pos: {
    marginBottom: 12,
  },
  headerCell: {
    fontWeight: 700,
    backgroundColor: "#F5F5F5",
    color: "#424242",
  },
  valorOriginal: {
    textDecoration: "line-through",
    color: "#9E9E9E",
    fontSize: "0.75rem",
    display: "block",
  },
  valorFinal: {
    fontWeight: 600,
    color: "#2E7D32",
  },
  contratoChip: {
    fontSize: "0.7rem",
    height: 24,
  },
}));

export default function PlanesClienteSeleccionado(props) {
  const { value, cliente } = props;
  const classes = styles();
  const [locale] = useState("esES");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // RTK Query - Obtener planes del cliente
  const {
    data: planes = [],
    isLoading,
    isError,
    error,
  } = useObtenerPlanesClienteQuery(cliente?.rut, {
    skip: !cliente?.rut || value !== 2,
  });

  // Ordenar planes: primero ACTIVO, luego INACTIVO
  const planesOrdenados = useMemo(() => {
    return [...planes].sort((a, b) => {
      if (a.estado === "ACTIVO" && b.estado !== "ACTIVO") return -1;
      if (a.estado !== "ACTIVO" && b.estado === "ACTIVO") return 1;
      return 0;
    });
  }, [planes]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div className={classes.pos}>
      {value === 2 && (
        <Card className={classes.root} variant="outlined">
          <CardContent>
            <Typography variant="h5" component="h2" className={classes.title}>
              Planes Asociados
            </Typography>

            {/* Estado de carga */}
            {isLoading && <TableSkeleton rows={4} columns={5} />}

            {/* Estado de error */}
            {isError && (
              <Box mt={1}>
                <Alert severity="error">
                  Error al cargar los planes. Por favor, intenta nuevamente.
                  {error?.data?.message && ` (${error.data.message})`}
                </Alert>
              </Box>
            )}

            {/* Sin planes */}
            {!isLoading && !isError && planesOrdenados.length === 0 && (
              <EmptyState
                title="Sin planes asociados"
                description="Este cliente no tiene planes registrados."
              />
            )}

            {/* Tabla de planes */}
            {!isLoading && !isError && planesOrdenados.length > 0 && (
              <>
                <Scrollbar>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small" aria-label="tabla de planes">
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.headerCell}>
                            Nombre Plan
                          </TableCell>
                          <TableCell
                            className={classes.headerCell}
                            align="center"
                          >
                            Frecuencia
                          </TableCell>
                          <TableCell
                            className={classes.headerCell}
                            align="right"
                          >
                            Valor
                          </TableCell>
                          <TableCell
                            className={classes.headerCell}
                            align="center"
                          >
                            Estado
                          </TableCell>
                          <TableCell
                            className={classes.headerCell}
                            align="center"
                          >
                            Contrato
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {planesOrdenados
                          .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                          .map((plan, index) => {
                            const tieneDescuento =
                              plan.descuento > 0 && plan.valorDesc > 0;
                            const contratoFirmado =
                              plan.contratoFirmado === "SI" ||
                              plan.contratoFirmado === "1" ||
                              plan.contratoFirmado === true;

                            return (
                              <TableRow key={`plan_${index}`} hover>
                                {/* Nombre del Plan */}
                                <TableCell>
                                  <Typography variant="body2">
                                    {plan.refTipoPlan}
                                  </Typography>
                                  {plan.fechaCreacion && (
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                    >
                                      Desde:{" "}
                                      {formatearFecha(
                                        plan.fechaCreacion,
                                        "DD/MM/YYYY",
                                        "YYYY/MM/DD",
                                      )}
                                    </Typography>
                                  )}
                                </TableCell>

                                {/* Frecuencia */}
                                <TableCell align="center">
                                  <Typography variant="body2">
                                    {plan.tipoFrecuencia}
                                  </Typography>
                                </TableCell>

                                {/* Valor */}
                                <TableCell align="right">
                                  {tieneDescuento ? (
                                    <>
                                      <span className={classes.valorOriginal}>
                                        {formatValorPlan(
                                          plan.valor,
                                          plan.tipoMoneda,
                                        )}
                                      </span>
                                      <Tooltip
                                        title={`${plan.descuento}% de descuento${plan.fechaTerminoDesc ? ` hasta ${formatearFecha(plan.fechaTerminoDesc, "DD/MM/YYYY", "YYYY/MM/DD")}` : ""}`}
                                      >
                                        <span className={classes.valorFinal}>
                                          {formatValorPlan(
                                            plan.valorDesc,
                                            plan.tipoMoneda,
                                          )}
                                        </span>
                                      </Tooltip>
                                    </>
                                  ) : (
                                    <Typography variant="body2">
                                      {formatValorPlan(
                                        plan.valor,
                                        plan.tipoMoneda,
                                      )}
                                    </Typography>
                                  )}
                                </TableCell>

                                {/* Estado */}
                                <TableCell align="center">
                                  <StatusBadge
                                    estado={plan.estado}
                                    tipo="plan"
                                  />
                                </TableCell>

                                {/* Contrato Firmado */}
                                <TableCell align="center">
                                  {contratoFirmado ? (
                                    <Tooltip title="Contrato firmado">
                                      <Chip
                                        icon={
                                          <CheckCircleOutlineIcon
                                            style={{ color: "#2E7D32" }}
                                          />
                                        }
                                        label="Firmado"
                                        size="small"
                                        className={classes.contratoChip}
                                        style={{
                                          backgroundColor: "#E8F5E9",
                                          color: "#2E7D32",
                                          borderColor: "#A5D6A7",
                                        }}
                                        variant="outlined"
                                      />
                                    </Tooltip>
                                  ) : (
                                    <Tooltip title="Sin contrato firmado">
                                      <Chip
                                        icon={
                                          <CancelOutlinedIcon
                                            style={{ color: "#E65100" }}
                                          />
                                        }
                                        label="Pendiente"
                                        size="small"
                                        className={classes.contratoChip}
                                        style={{
                                          backgroundColor: "#FFF3E0",
                                          color: "#E65100",
                                          borderColor: "#FFCC80",
                                        }}
                                        variant="outlined"
                                      />
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Scrollbar>

                <ThemeProvider
                  theme={(outerTheme) =>
                    createTheme(outerTheme, locales[locale])
                  }
                >
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={planesOrdenados.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </ThemeProvider>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
